import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_2 = { class: "flex items-center space-x-4" }
const _hoisted_3 = { class: "grid grid-cols-1 gap-4 space-y-4 md:grid-cols-3 md:space-y-0" }
const _hoisted_4 = { class: "mb-2 text-lg font-semibold text-gray-800" }
const _hoisted_5 = { class: "gap-4 space-y-4 md:grid md:grid-cols-3 md:space-y-0" }
const _hoisted_6 = { class: "flex flex-col" }
const _hoisted_7 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_8 = {
  key: 0,
  class: "text-danger bg-danger-100 p-2 text-sm shadow"
}
const _hoisted_9 = {
  key: 1,
  controls: "",
  class: "mt-2 w-full"
}
const _hoisted_10 = ["src"]
const _hoisted_11 = { class: "mb-2 text-lg font-semibold text-gray-800" }
const _hoisted_12 = { class: "mt-2 flex justify-end border-t border-gray-300 pt-4" }
const _hoisted_13 = { class: "mb-2 text-lg font-semibold text-gray-800" }
const _hoisted_14 = { class: "gap-4 space-y-4 md:grid md:grid-cols-3 md:space-y-0" }
const _hoisted_15 = { class: "flex flex-col" }
const _hoisted_16 = { class: "mb-2 block text-sm font-medium text-gray-800" }
const _hoisted_17 = {
  key: 0,
  class: "text-danger bg-danger-100 p-2 text-sm shadow"
}
const _hoisted_18 = {
  key: 1,
  controls: "",
  class: "mt-2 w-full"
}
const _hoisted_19 = ["src"]

import { ref, computed, reactive, watch, onMounted, Ref, ComponentPublicInstance } from 'vue';
import { useRoute } from 'vue-router';
import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { AxiosResponse } from 'axios';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { ConnectFlowSurveyService, VaultService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { getAudioFormat } from '@/utils/audio';

import {
    Permission,
    FormAction,
    ConnectFlowSurveyModel,
    ConnectFlowSurveyFormState,
} from '@/types';

import { required, requiredIf, minLength, maxLength } from '@/utils/validators';
import { ConnectFlowSurveyQuestionForm } from '@/views/pages/connect-flow-survey/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConnectFlowSurveyFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props) {

const props = __props;

const route = useRoute();
const uiStore = useUiStore();

const formState = reactive<ConnectFlowSurveyFormState>({
    name: null,
    textToAudio: null,
    audioFilePath: null,
    questions: [],
    isActive: true,
    finalTextToAudio: null,
    finalAudioFilePath: null,
});

const file = ref<File | null>(null);
const audioPreview = ref<string | null>(null);
const questionRefs = ref<Record<string, InstanceType<typeof ConnectFlowSurveyQuestionForm> | null>>(
    {},
);
const finalFile = ref<File | null>(null);
const finalAudioPreview = ref<string | null>(null);

const rules = computed<ValidationArgs<ConnectFlowSurveyFormState>>(() => ({
    name: { required, maxLength: maxLength(500) },
    textToAudio: {
        required: requiredIf(() => !formState.audioFilePath),
        maxLength: maxLength(500),
    },
    finalTextToAudio: {
        required: requiredIf(() => !formState.audioFilePath),
        maxLength: maxLength(500),
    },
    audioFilePath: {
        required: requiredIf(() => formState.textToAudio?.length === 0),
    },
    finalAudioFilePath: {
        required: requiredIf(() => formState.finalTextToAudio?.length === 0),
    },
    questions: { required, minLength: minLength(1) },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<ConnectFlowSurveyFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchConnectFlowSurvey = async (id: number) => {
    try {
        await setLoading(true);
        const { data } = await ConnectFlowSurveyService.get<ConnectFlowSurveyModel>(id);

        formState.name = data.name;
        formState.textToAudio = data.textToAudio;
        formState.audioFilePath = data.audioFilePath;
        formState.questions = data.questions.map((question) => ({
            id: question.id,
            _key: `question_${question.id}`,
            label: question.label,
            rangeAnswer: question.rangeAnswer,
            textToAudio: question.textToAudio || null,
            audioFile: null,
            audioFilePath: question.audioFilePath || null,
        }));
        formState.isActive = data.isActive;
        formState.finalTextToAudio = data.finalTextToAudio;
        formState.finalAudioFilePath = data.finalAudioFilePath;
        await loadAudio();
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const loadAudio = async () => {
    if (formState.audioFilePath) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.audioFilePath);
            const blob = new Blob([data], { type: getAudioFormat(formState.audioFilePath) });
            file.value = new File([blob], formState.audioFilePath, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }

    if (formState.finalAudioFilePath) {
        try {
            const { data } = await VaultService.getAudioAsset(formState.finalAudioFilePath);
            const blob = new Blob([data], { type: getAudioFormat(formState.finalAudioFilePath) });
            finalFile.value = new File([blob], formState.finalAudioFilePath, {
                type: blob.type,
                lastModified: Date.now(),
            });
        } catch (e) {
            toastServiceError(e);
        }
    }
};

const onSubmit = async () => {
    const questionValidationResults = await Promise.all(
        Object.values(questionRefs.value).map((ref) => ref?.validate?.()),
    );

    const allQuestionsValid = questionValidationResults.every((valid) => valid);
    const isFormValid = await v$.value.$validate();

    if (!isFormValid || !allQuestionsValid) return;

    await setLoading(true);

    const formData = new FormData();
    formData.append('name', formState.name || '');
    formData.append('textToAudio', formState.textToAudio || '');
    if (file.value) formData.append('audio', file.value);
    formData.append('finalTextToAudio', formState.finalTextToAudio || '');
    if (finalFile.value) formData.append('finalAudio', finalFile.value);
    formData.append('isActive', formState.isActive.toString());

    formState.questions.forEach((question, index) => {
        if (question.id) formData.append(`questions[${index}][id]`, question.id.toString());
        formData.append(`questions[${index}][label]`, question.label || '');
        formData.append(`questions[${index}][rangeAnswer]`, question.rangeAnswer || '');
        formData.append(`questions[${index}][textToAudio]`, question.textToAudio || '');
        if (question.audioFile)
            formData.append(`questions[${index}][question-audio]`, question.audioFile);
        if (question.audioFilePath)
            formData.append(`questions[${index}][audioFilePath]`, question.audioFilePath || '');
    });

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewConnectFlowSurveyById', params: { id } });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await ConnectFlowSurveyService.update(id, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await ConnectFlowSurveyService.create(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const setQuestionRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        questionRefs.value[key] = el as InstanceType<typeof ConnectFlowSurveyQuestionForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // questionRefs.value[key] = null as any;
        delete questionRefs.value[key];
    }
};

const addQuestion = () => {
    formState.questions.push({
        _key: `tmp_${crypto.randomUUID()}`,
        label: null,
        rangeAnswer: null,
        textToAudio: null,
        audioFile: null,
        audioFilePath: null,
    });
};

const removeQuestion = (index: number) => {
    formState.questions.splice(index, 1);
};

watch([file], () => {
    if (file.value) {
        formState.audioFilePath = file.value.name;
        audioPreview.value = URL.createObjectURL(file.value);
        formState.textToAudio = null;
    } else {
        formState.audioFilePath = '';
        audioPreview.value = null;
    }
});

watch([finalFile], () => {
    if (finalFile.value) {
        formState.finalAudioFilePath = finalFile.value.name;
        finalAudioPreview.value = URL.createObjectURL(finalFile.value);
        formState.finalTextToAudio = null;
    } else {
        formState.finalAudioFilePath = '';
        finalAudioPreview.value = null;
    }
});

onMounted(async () => {
    if (props.action === FormAction.EDIT) {
        const surveyId = Number(route.params.id);
        if (!isNaN(surveyId)) fetchConnectFlowSurvey(surveyId);
    } else {
        addQuestion();
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiTextInput = _resolveComponent("UiTextInput")!
  const _component_UiCheckboxInput = _resolveComponent("UiCheckboxInput")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiTextArea = _resolveComponent("UiTextArea")!
  const _component_UiAudioUpload = _resolveComponent("UiAudioUpload")!
  const _component_UiCheckDirtyBeforeRouteLeave = _resolveComponent("UiCheckDirtyBeforeRouteLeave")!

  return (_openBlock(), _createElementBlock("form", {
    class: "space-y-4 p-4 sm:p-6 lg:p-8",
    autocomplete: "off",
    onSubmit: _withModifiers(onSubmit, ["prevent"])
  }, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_1, _toDisplayString(_ctx.$t('connectFlowSurvey.' + props.action + '.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListConnectFlowSurvey' } }),
          (
                            (props.action == _unref(FormAction).CREATE &&
                                _ctx.$can(_unref(Permission).CREATE_CONNECT_FLOW_SURVEY)) ||
                            (props.action == _unref(FormAction).EDIT &&
                                _ctx.$can(_unref(Permission).EDIT_CONNECT_FLOW_SURVEY))
                        )
            ? (_openBlock(), _createBlock(_component_UiButton, {
                key: 0,
                type: "submit",
                variant: "primary",
                "text-variant": "white"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiTextInput, {
            modelValue: _unref(v$).name.$model,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(v$).name.$model) = $event)),
            name: "name",
            type: "text",
            label: _ctx.$t('connectFlowSurvey.form.labels.name'),
            errors: _unref(v$).name.$errors,
            onBlur: _unref(v$).name.$touch
          }, null, 8, ["modelValue", "label", "errors", "onBlur"]),
          _createVNode(_component_UiCheckboxInput, {
            modelValue: _unref(v$).isActive.$model,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).isActive.$model) = $event)),
            name: "isActive",
            label: _ctx.$t('connectFlowSurvey.form.labels.isActive'),
            class: "md:pt-6"
          }, null, 8, ["modelValue", "label"])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("h2", _hoisted_4, [
          _createTextVNode(_toDisplayString(_ctx.$t('connectFlowSurvey.form.section.initialMessage.title')) + " ", 1),
          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_UiTextArea, {
            id: "textToAudio",
            name: "textToAudio",
            modelValue: formState.textToAudio,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formState.textToAudio) = $event)),
            label: _ctx.$t('connectFlowSurvey.form.labels.textToAudio'),
            placeholder: _ctx.$t('connectFlowSurvey.form.labels.textToAudioPlaceholder'),
            rows: 4,
            disabled: file.value != undefined,
            errors: _unref(v$).textToAudio.$errors,
            class: "col-span-2"
          }, null, 8, ["modelValue", "label", "placeholder", "disabled", "errors"]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("label", _hoisted_7, _toDisplayString(_ctx.$t('connectFlowSurvey.form.labels.audioUpload')), 1),
            _createVNode(_component_UiAudioUpload, {
              file: file.value,
              "onUpdate:file": _cache[3] || (_cache[3] = ($event: any) => ((file).value = $event)),
              "text-rule": _ctx.$t('connectFlowSurvey.form.labels.uploadRules'),
              errors: _unref(v$).audioFilePath.$errors
            }, null, 8, ["file", "text-rule", "errors"]),
            (_unref(v$).audioFilePath.$errors && _unref(v$).audioFilePath.$errors.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).audioFilePath.$errors, (error) => {
                    return (_openBlock(), _createElementBlock("p", {
                      key: error.$uid
                    }, _toDisplayString(error.$message), 1))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (audioPreview.value)
              ? (_openBlock(), _createElementBlock("audio", _hoisted_9, [
                  _createElementVNode("source", { src: audioPreview.value }, null, 8, _hoisted_10),
                  _cache[7] || (_cache[7] = _createTextVNode(" Your browser does not support the audio element. ", -1))
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("h2", _hoisted_11, [
          _createTextVNode(_toDisplayString(_ctx.$t('connectFlowSurvey.form.section.questions.title')) + " ", 1),
          _cache[8] || (_cache[8] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
        ]),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formState.questions, (item, index) => {
          return (_openBlock(), _createBlock(_unref(ConnectFlowSurveyQuestionForm), {
            key: item._key,
            index: index,
            modelValue: formState.questions[index],
            "onUpdate:modelValue": ($event: any) => ((formState.questions[index]) = $event),
            "show-remove": formState.questions.length > 1,
            ref_for: true,
            ref: (el) => setQuestionRef(el, item._key),
            onRemove: removeQuestion
          }, null, 8, ["index", "modelValue", "onUpdate:modelValue", "show-remove"]))
        }), 128)),
        _createElementVNode("div", _hoisted_12, [
          _createVNode(_component_UiButton, {
            variant: "primary",
            "text-variant": "white",
            onClick: addQuestion
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('connectFlowSurvey.form.actions.addQuestion')), 1)
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("h2", _hoisted_13, [
          _createTextVNode(_toDisplayString(_ctx.$t('connectFlowSurvey.form.section.finalMessage.title')) + " ", 1),
          _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-xs" }, "*", -1))
        ]),
        _createElementVNode("div", _hoisted_14, [
          _createVNode(_component_UiTextArea, {
            id: "textToAudio",
            name: "textToAudio",
            modelValue: formState.finalTextToAudio,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formState.finalTextToAudio) = $event)),
            label: _ctx.$t('connectFlowSurvey.form.labels.textToAudio'),
            placeholder: _ctx.$t('connectFlowSurvey.form.labels.textToAudioPlaceholder'),
            rows: 4,
            disabled: finalFile.value != undefined,
            errors: _unref(v$).finalTextToAudio.$errors,
            class: "col-span-2"
          }, null, 8, ["modelValue", "label", "placeholder", "disabled", "errors"]),
          _createElementVNode("div", _hoisted_15, [
            _createElementVNode("label", _hoisted_16, _toDisplayString(_ctx.$t('connectFlowSurvey.form.labels.audioUpload')), 1),
            _createVNode(_component_UiAudioUpload, {
              file: finalFile.value,
              "onUpdate:file": _cache[5] || (_cache[5] = ($event: any) => ((finalFile).value = $event)),
              "text-rule": _ctx.$t('connectFlowSurvey.form.labels.uploadRules'),
              errors: _unref(v$).finalAudioFilePath.$errors
            }, null, 8, ["file", "text-rule", "errors"]),
            (_unref(v$).finalAudioFilePath.$errors && _unref(v$).finalAudioFilePath.$errors.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(v$).finalAudioFilePath.$errors, (error) => {
                    return (_openBlock(), _createElementBlock("p", {
                      key: error.$uid
                    }, _toDisplayString(error.$message), 1))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (finalAudioPreview.value)
              ? (_openBlock(), _createElementBlock("audio", _hoisted_18, [
                  _createElementVNode("source", { src: finalAudioPreview.value }, null, 8, _hoisted_19),
                  _cache[10] || (_cache[10] = _createTextVNode(" Your browser does not support the audio element. ", -1))
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_UiCheckDirtyBeforeRouteLeave, {
      dirty: _unref(v$).$anyDirty
    }, null, 8, ["dirty"])
  ], 32))
}
}

})